import * as React from 'react';
import { t } from '@reos/i18n';
import {
  confirmCaseListingDeal,
  fetchCaseListingDeal,
  type CaseListingDeal,
  type ListingDealStatus,
} from '../lib/connectors.js';
import { DomainApiError } from '../lib/api-error.js';

/**
 * Krok LIST1 — INZERAT PO REZERVACI A PRODEJI (zalozka Inzerce zakazky).
 *
 * Nalez auditu 14. 10. 2026 c. 14: prodana nemovitost zustavala na portalech.
 * Blok ukazuje STAV inzeratu (Aktivní / Rezervováno / Prodáno / Staženo) a
 * kdyz je co resit, NABIDNE akci. Nic se neodesila bez kliku makere
 * (zadani bod 3) — potvrzeni je jedno, a teprve ono vola API.
 *
 * Popisky stavu zijí v `webAdmin.listingDeal.status.*`, tedy na JEDNOM miste
 * pro tenhle blok, prehled inzeratu v /portaly i radek na Dnesku.
 */

const cs = { locale: 'cs' } as const;

export function listingDealStatusLabel(status: ListingDealStatus): string {
  return t(`webAdmin.listingDeal.status.${status}`, cs);
}

function errorText(cause: unknown): string {
  return cause instanceof DomainApiError || cause instanceof Error ? cause.message : String(cause);
}

export function ListingDealBlock({ caseId }: { readonly caseId: string }): React.JSX.Element {
  const [deal, setDeal] = React.useState<CaseListingDeal | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [queued, setQueued] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback((): void => {
    fetchCaseListingDeal(caseId).then(
      (loadedDeal) => {
        setDeal(loadedDeal);
        setLoaded(true);
      },
      /*
       * 404 = zakazka jeste zadny inzerat nema. To NENI chyba, kterou by
       * makler mel cist — blok se jen nevykresli.
       */
      () => {
        setDeal(null);
        setLoaded(true);
      },
    );
  }, [caseId]);

  React.useEffect(load, [load]);

  function confirm(kind: 'RESERVED' | 'SOLD', mode?: 'WITHDRAW' | 'KEEP_SOLD'): void {
    setBusy(true);
    setError(null);
    setQueued(null);
    confirmCaseListingDeal(caseId, { kind, ...(mode === undefined ? {} : { mode }) }).then(
      (result) => {
        setDeal(result.view);
        setQueued(result.bulk.queued);
        setBusy(false);
      },
      (cause: unknown) => {
        setError(errorText(cause));
        setBusy(false);
      },
    );
  }

  if (!loaded || deal === null) return <></>;

  const live = deal.portals.filter((portal) => portal.live).map((portal) => portal.portal);
  const offer = deal.offer;
  const offerPortals = offer === null ? '' : offer.portals.join(', ');

  return (
    <section className="rh-a2-card" data-testid="case-listing-deal">
      <div className="rh-a2-card__head">
        <h2>{t('webAdmin.listingDeal.heading', cs)}</h2>
      </div>
      <p className="rh-a2-meta" style={{ margin: '0 0 6px' }}>
        {t('webAdmin.listingDeal.statusLabel', cs)}:{' '}
        <strong data-testid="case-listing-deal-status">{listingDealStatusLabel(deal.status)}</strong>
      </p>
      <p className="rh-a2-meta" style={{ margin: '0 0 6px' }} data-testid="case-listing-deal-portals">
        {live.length === 0
          ? t('webAdmin.listingDeal.noPortals', cs)
          : t('webAdmin.listingDeal.portalsLine', { ...cs, values: { portals: live.join(', ') } })}
      </p>
      {deal.soldKeepUntil !== null && (
        <p className="rh-a2-meta" style={{ margin: '0 0 6px' }} data-testid="case-listing-deal-keep-until">
          {t('webAdmin.listingDeal.keepUntilLine', {
            ...cs,
            values: {
              date: new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium' }).format(
                new Date(deal.soldKeepUntil),
              ),
            },
          })}
        </p>
      )}

      {/* Krok NAP2 bod A4 — stazeni planovacem se nestane potichu: makler vidi, ze a kdy. */}
      {deal.soldKeepWithdrawnAt !== null && (
        <p className="rh-a2-meta" style={{ margin: '0 0 6px' }} data-testid="case-listing-deal-auto-withdrawn">
          {t('webAdmin.listingDeal.autoWithdrawnLine', {
            ...cs,
            values: {
              date: new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium' }).format(
                new Date(deal.soldKeepWithdrawnAt),
              ),
            },
          })}
        </p>
      )}

      {offer !== null && (
        <div data-testid={`case-listing-deal-offer-${offer.kind}`} style={{ borderTop: '1px solid var(--rh-rule)', paddingTop: 10 }}>
          <h3 style={{ fontSize: 'var(--rh-size-footnote)', margin: '0 0 2px' }}>
            {t(
              offer.kind === 'SOLD'
                ? 'webAdmin.listingDeal.offerSoldTitle'
                : 'webAdmin.listingDeal.offerReservedTitle',
              cs,
            )}
          </h3>
          <p className="rh-a2-meta" style={{ margin: '0 0 8px' }}>
            {t(
              offer.kind === 'SOLD'
                ? 'webAdmin.listingDeal.offerSoldDetail'
                : 'webAdmin.listingDeal.offerReservedDetail',
              { ...cs, values: { portals: offerPortals } },
            )}
          </p>
          {offer.kind === 'SOLD' ? (
            <>
              {/* Vychozi volba je STAZENI (zadani bod 2) — proto je prvni. */}
              <button
                type="button"
                className="rh-a2-btn rh-a2-btn--primary"
                disabled={busy}
                onClick={() => { confirm('SOLD', 'WITHDRAW'); }}
                data-testid="case-listing-deal-confirm-withdraw"
              >
                {t('webAdmin.listingDeal.offerSoldWithdraw', cs)}
              </button>{' '}
              <button
                type="button"
                className="rh-a2-btn"
                disabled={busy}
                onClick={() => { confirm('SOLD', 'KEEP_SOLD'); }}
                data-testid="case-listing-deal-confirm-keep"
              >
                {t('webAdmin.listingDeal.offerSoldKeep', {
                  ...cs,
                  values: { days: offer.keepDefaultDays },
                })}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="rh-a2-btn rh-a2-btn--primary"
              disabled={busy}
              onClick={() => { confirm('RESERVED'); }}
              data-testid="case-listing-deal-confirm-reserved"
            >
              {t('webAdmin.listingDeal.offerReservedConfirm', cs)}
            </button>
          )}
        </div>
      )}

      {busy && (
        <p role="status" className="rh-a2-meta" style={{ margin: '8px 0 0' }} data-testid="case-listing-deal-pending">
          {t('webAdmin.listingDeal.pending', cs)}
        </p>
      )}
      {queued !== null && (
        <p role="status" className="rh-a2-meta" style={{ margin: '8px 0 0' }} data-testid="case-listing-deal-queued">
          {t('webAdmin.listingDeal.confirmedQueued', { ...cs, values: { count: queued } })}
        </p>
      )}
      {error !== null && (
        <p role="alert" className="rh-a2-error" style={{ margin: '8px 0 0' }} data-testid="case-listing-deal-error">
          {t('webAdmin.listingDeal.confirmError', cs)}
        </p>
      )}
    </section>
  );
}
